import { Pipe, PipeTransform } from '@angular/core';

interface DiferenciaFechas {
  anyos: number;
  meses: number;
  dias: number;
  horas: number;
  minutos: number;
}

// Añadimos 'superCorto' a los formatos aceptados
type FormatoFecha = 'completo' | 'corto' | 'superCorto';

@Pipe({
  name: 'cuandoFechas',
  standalone: false
})
export class CuandoFechasPipe implements PipeTransform {

  transform(fechaInicio: string | Date | null | undefined, formato: FormatoFecha = 'completo'): string {
    if (!fechaInicio) return '';

    const f1 = new Date(fechaInicio);
    const f2 = new Date();

    const prefijoDia = this.obtenerPrefijoDia(f1, f2);
    const t = this.diferenciaFechas(f1, f2);
    const tieneHora = this.tieneHora(fechaInicio);
    const hora = tieneHora ? this.formatoHora24(f1) : '';

    const esFechaCercana = prefijoDia === 'Hoy' || prefijoDia === 'Ayer' || prefijoDia === 'Mañana';
    
    let inicioFrase = '';
    if (!esFechaCercana) {
      // Pasamos el formato actual a la función encargada del renderizado
      inicioFrase = this.formatoFechaCompleta(f1, f2, formato);
    } else {
      inicioFrase = prefijoDia;
    }

    const separador = tieneHora ? (inicioFrase ? ` ${hora}` : hora) : '';
    const bloqueTemporal = `${inicioFrase}${separador}`;

    // =======================================================================
    // CONTROL SUPER CORTO: Si es 'superCorto', cortamos aquí y no añade el "hace X"
    // =======================================================================
    if (formato === 'superCorto') {
      return bloqueTemporal;
    }

    // Lógica para fechas futuras (Mañana, etc.)
    if (f1.getTime() > f2.getTime()) {
      return bloqueTemporal;
    }

    // Lógica para fechas pasadas
    if (t.anyos > 0) {
      return this.unirFrase(bloqueTemporal, t.anyos === 1 ? 'hace 1 año' : `hace ${t.anyos} años`);
    }

    if (t.meses > 0) {
      return this.unirFrase(bloqueTemporal, t.meses === 1 ? 'hace 1 mes' : `hace ${t.meses} meses`);
    }

    if (t.dias > 14) {
      return this.unirFrase(bloqueTemporal, `hace ${Math.round(t.dias / 7)} semanas`);
    }

    if (t.dias > 7) {
      return this.unirFrase(bloqueTemporal, 'hace 1 semana');
    }

    if (t.dias >= 1 || t.horas >= 24) {
      const diasCalculados = t.dias > 0 ? t.dias : Math.floor(t.horas / 24);
      return this.unirFrase(bloqueTemporal, diasCalculados === 1 ? 'hace 1 día' : `hace ${diasCalculados} días`);
    }

    if (prefijoDia === 'Ayer') {
      if (t.horas > 1 && tieneHora) {
        return this.unirFrase(bloqueTemporal, `hace ${t.horas} horas`);
      }
      return bloqueTemporal;
    }

    if (t.horas > 1 && tieneHora) {
      return this.unirFrase(bloqueTemporal, `hace ${t.horas} horas`);
    }

    if (t.horas === 1 && tieneHora) {
      return this.unirFrase(bloqueTemporal, 'hace 1 hora');
    }

    if (t.minutos > 1 && tieneHora) {
      return this.unirFrase(bloqueTemporal, `hace ${t.minutos} minutos`);
    }

    if (t.minutos === 1 && tieneHora) {
      return this.unirFrase(bloqueTemporal, 'hace 1 minuto');
    }

    return bloqueTemporal ? this.unirFrase(bloqueTemporal, tieneHora ? 'ahora mismo' : '') : (tieneHora ? 'ahora mismo' : '');
  }

  private unirFrase(bloqueTemporal: string, textoRelativo: string): string {
    if (!textoRelativo) return bloqueTemporal;
    return bloqueTemporal ? `${bloqueTemporal} · ${textoRelativo}` : textoRelativo;
  }

  private obtenerPrefijoDia(f1: Date, f2: Date): string {
    const fecha = new Date(f1.getFullYear(), f1.getMonth(), f1.getDate()).getTime();
    const hoy = new Date(f2.getFullYear(), f2.getMonth(), f2.getDate()).getTime();
    
    const ayerObj = new Date(f2);
    ayerObj.setDate(ayerObj.getDate() - 1);
    const ayer = new Date(ayerObj.getFullYear(), ayerObj.getMonth(), ayerObj.getDate()).getTime();

    const mañanaObj = new Date(f2);
    mañanaObj.setDate(mañanaObj.getDate() + 1);
    const mañana = new Date(mañanaObj.getFullYear(), mañanaObj.getMonth(), mañanaObj.getDate()).getTime();

    if (fecha === hoy) return 'Hoy';
    if (fecha === ayer) return 'Ayer';
    if (fecha === mañana) return 'Mañana';
    
    return '';
  }

  private formatoFechaCompleta(fecha: Date, ahora: Date, formato: FormatoFecha): string {
    let diaSemana = fecha.toLocaleDateString('es-ES', { weekday: 'long' });
    diaSemana = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);

    const opciones: Intl.DateTimeFormatOptions = {
      day: 'numeric'
    };

    // Si es 'corto' o 'superCorto', solo dejamos el número del día.
    // Si es 'completo', añadimos mes y año.
    if (formato === 'completo') {
      opciones.month = 'long';
      if (fecha.getFullYear() !== ahora.getFullYear()) {
        opciones.year = 'numeric';
      }
    }

    let fechaLarga = fecha.toLocaleDateString('es-ES', opciones);
    fechaLarga = fechaLarga.replace(/ de /g, ' ');

    return `${diaSemana} ${fechaLarga}`;
  }

  private formatoHora24(fecha: Date): string {
    const horas = fecha.getHours().toString().padStart(2, '0');
    const minutos = fecha.getMinutes().toString().padStart(2, '0');

    return `${horas}:${minutos}`;
  }

  private formatoHora12(fecha: Date): string {
    let horas = fecha.getHours();
    const minutos = fecha.getMinutes().toString().padStart(2, '0');
    const ampm = horas >= 12 ? 'PM' : 'AM';

    horas = horas % 12;
    horas = horas === 0 ? 12 : horas;

    return `${horas}:${minutos} ${ampm}`;
  }

  private diferenciaFechas(f1: Date, f2: Date): DiferenciaFechas {
    let diffMs = f2.getTime() - f1.getTime();
    if (diffMs < 0) diffMs = 0;

    const minutos = Math.floor(diffMs / 60000);
    const horas   = Math.floor(diffMs / 3600000);
    const dias    = Math.floor(diffMs / 86400000);

    let meses =
      (f2.getFullYear() - f1.getFullYear()) * 12 +
      (f2.getMonth() - f1.getMonth());

    if (f2.getDate() < f1.getDate()) {
      meses--;
    }

    const anyos = Math.floor(meses / 12);

    return {
      anyos,
      meses: meses % 12,
      dias,
      horas,
      minutos
    };
  }

  private tieneHora(fecha: string | Date): boolean {
    if (typeof fecha === 'string') {
      return fecha.includes(':');
    }
    return (
      fecha.getHours() !== 0 ||
      fecha.getMinutes() !== 0 ||
      fecha.getSeconds() !== 0
    );
  }
}